import { API } from "../utils/constants";

// Get list of all users (admin only)
export const getUsersList = async (uid: string, page = 1, limit = 20) => {
  try {
    const response = await fetch(`${API}/admin/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uid }),
    });

    const data = await response.json();

    if (data.error) {
      console.error("Error fetching users list:", data.error);
      return {
        users: [],
        total: 0,
        page: 1,
        error: data.error,
      };
    }

    // Ensure data is an array before slicing
    const users = Array.isArray(data) ? data : [];

    return {
      users: users.slice((page - 1) * limit, page * limit),
      total: users.length,
      page,
      error: null,
    };
  } catch (error) {
    console.error("Network error fetching users list:", error);
    return {
      users: [],
      total: 0,
      page: 1,
      error: "Network error",
    };
  }
};
// Resolve UID from Telegram ID
export const getUserId = async (telegramId: string | number) => {
  try {
    const response = await fetch(API + "/getUserId", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        telegramId: telegramId,
      }),
    });

    const data = await response.json();

    if (data?.error) {
      return null;
    }

    // Expect backend to return { uid: "..." }
    return data?.uid || null;
  } catch (error) {
    console.error("Error resolving UID from telegramId:", error);
    return null;
  }
};

// Fetch User Data by ID
export const getUser = async (uid: string | number) => {
  try {
    const response = await fetch(API + "/getUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uid: uid,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });

    const data = await response.json();

    if (data?.error) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error fetching user data:", error);
    return null;
  }
};

// Fetch Top Users for Leaderboard
export const getTopUsers = async (uid: string) => {
  try {
    const response = await fetch(API + "/getTopUsers?limit=100", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uid: uid,
      }),
    });

    const data = await response.json();

    if (data.error) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error fetching top users:", error);
    return null;
  }
};

// Fetch Friends of the User
export const getFriends = async (uid: string) => {
  try {
    const response = await fetch(API + "/getFriends", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uid: uid,
      }),
    });

    const data = await response.json();

    if (data.error) {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error fetching friends:", error);
    return null;
  }
};

// Update Wallet Address for the User
export const setWalletAddressApi = async (
  uid: string,
  walletAddress: string
) => {
  const response = await fetch(API + "/updateAddress", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid: uid,
      address: walletAddress,
    }),
  });

  const data = await response.json();

  if (response.ok) {
    return data;
  }

  return response;
};

// Update Twitter Handle for the User
export const setHandleApi = async (
  uid: string,
  handle: string,
  socialMedia: string
) => {
  const response = await fetch(API + "/updateHandle", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid: uid,
      handle: handle,
      socialMedia: socialMedia, // 'twitter' or 'instagram'
    }),
  });

  const data = await response.json();

  if (response.ok) {
    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  }

  return response;
};

export const updateWalletAddress = async (
  uid: string,
  walletAddress: string
) => {
  const response = await fetch(API + "/updateAddress", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid: uid,
      address: walletAddress,
    }),
  });

  const data = await response.json();

  if (response.ok) {
    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  }

  return response;
};

// Updated mineTokens function to match the new API
export const mineTokens = async (uid: string) => {
  try {
    const response = await fetch(API + "/mineTokens", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uid: uid,
      }),
    });

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    return data; // Returns updated user data with new points and lastClaimedRewardTime
  } catch (error) {
    console.error("Error mining tokens:", error);
    throw error;
  }
};
// User Management
export const banUser = async (
  uidToBan: string | number,
  reason: string,
  uid: string
) => {
  const response = await fetch(API + "/admin/banUser", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uidToBan,
      uid,
      reason,
    }),
  });

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error);
  }

  return data.success;
};

export const unbanUser = async (uidToBan: string | number, uid: string) => {
  const response = await fetch(API + "/admin/unbanUser", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uidToBan,
      uid,
    }),
  });

  const data = await response.json();

  if (data.error) {
    return false;
  }

  return data.success;
};

export const updateUserBalance = async (
  targetUserUid: string | number,
  newBalance: number,
  uid: string
) => {
  const response = await fetch(API + "/admin/updateUserBalance", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid,
      targetUserUid,
      newBalance,
    }),
  });

  const data = await response.json();

  if (data.error) {
    return null;
  }

  return data;
};

export const updateUserLifeTime = async (
  targetUserUid: string | number,
  newLifeTime: number,
  uid: string
) => {
  const response = await fetch(API + "/admin/updateUserLifeTime", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid,
      targetUserUid,
      newLifeTime,
    }),
  });

  const data = await response.json();

  if (data.error) {
    return null;
  }

  return data;
};
